import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { Button } from '../../../core/button';
import { Box } from '../../../core/miscellaneous/Box';
import { Text } from '../../../core/typography/Text';
import { Stepper } from '../../../core/navigation/Stepper';
import { Group } from '../../../core/layout/Group';
import classes from './WorkflowStepper.module.css';

export interface WorkflowStepperStep {
  /** Unique identifier for the step */
  id: string;
  /** Display label for the step */
  label: string;
  /** Whether this step is completed */
  completed?: boolean;
  /** Whether this step is disabled (not clickable) */
  disabled?: boolean;
  /** Optional nested steps within this step (composite pattern) */
  steps?: WorkflowStepperStep[];
}

export interface WorkflowStepperProps {
  /** Array of workflow steps */
  readonly steps: readonly WorkflowStepperStep[];
  /** Index of the currently active main step */
  readonly activeStepIndex: number;
  /** ID of the currently active sub-step (if any) */
  readonly activeSubStepId?: string | null;
  /** Callback when a main step is clicked */
  readonly onStepClick?: (stepIndex: number) => void;
  /** Callback when a sub-step is clicked */
  readonly onSubStepClick?: (stepIndex: number, subStepId: string) => void;
  /** Additional CSS class name */
  readonly className?: string;
}

interface StepListProps {
  readonly steps: WorkflowStepperStep[];
  readonly activeSubStepId: string | null | undefined;
  readonly stepIndex: number;
  readonly onSubStepClick?: (stepIndex: number, subStepId: string) => void;
}

function StepList({
  steps,
  activeSubStepId,
  stepIndex,
  onSubStepClick,
}: StepListProps) {
  const handleStepClick = (event: React.MouseEvent, stepId: string) => {
    event.stopPropagation();
    onSubStepClick?.(stepIndex, stepId);
  };

  return (
    <Box className={classes.subStepList}>
      {steps.map((step) => {
        const isActive = activeSubStepId === step.id;
        const isCompleted = step.completed === true;
        const isDisabled = step.disabled === true;

        if (isDisabled) {
          return (
            <Box key={step.id} className={classes.subStepDisabled}>
              <Text size="sm">{step.label}</Text>
            </Box>
          );
        }

        return (
          <Button
            key={step.id}
            variant="subtle"
            fullWidth
            justify="flex-start"
            size="sm"
            className={`${classes.subStep} ${
              isActive ? classes.subStepActive : ''
            }`}
            onClick={(event) => handleStepClick(event, step.id)}
            aria-current={isActive ? 'step' : undefined}
          >
            <Group gap="xs">
              {isCompleted ? (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className={classes.subStepIconCompleted}
                  aria-hidden="true"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCircle}
                  className={`${classes.subStepIconIncomplete} ${
                    isActive ? classes.subStepIconIncompleteActive : ''
                  }`}
                  aria-hidden="true"
                />
              )}
              <Text size="sm">{step.label}</Text>
            </Group>
          </Button>
        );
      })}
    </Box>
  );
}

export function WorkflowStepper({
  steps,
  activeStepIndex,
  activeSubStepId,
  onStepClick,
  onSubStepClick,
  className,
}: WorkflowStepperProps) {
  const handleStepClick = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (step.steps && step.steps.length > 0) {
      const firstEnabledStep = step.steps.find(
        (nestedStep) => nestedStep.disabled !== true
      );
      if (firstEnabledStep && onSubStepClick) {
        onSubStepClick(stepIndex, firstEnabledStep.id);
      }
    }
    onStepClick?.(stepIndex);
  };

  return (
    <Box className={className}>
      <Stepper
        active={activeStepIndex}
        onStepClick={handleStepClick}
        orientation="vertical"
        size="lg"
      >
        {steps.map((step, index) => {
          return (
            <Stepper.Step
              key={step.id}
              label={<Text mt="md">{step.label}</Text>}
              description={
                step.steps && step.steps.length > 0 ? (
                  <StepList
                    steps={step.steps}
                    activeSubStepId={activeSubStepId}
                    stepIndex={index}
                    onSubStepClick={onSubStepClick}
                  />
                ) : undefined
              }
            />
          );
        })}
      </Stepper>
    </Box>
  );
}

WorkflowStepper.displayName = 'WorkflowStepper';
